import React from 'react';
import { Link } from 'react-router-dom';
import { Award, Users, Truck, Calendar, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const timeline = [
  {
    year: '1977',
    title: 'Fondation',
    description: 'Jean Lebleu fonde Le Bleu des Champs à Saint-Césaire avec sa femme Huguette. L\'entreprise se spécialise dans la réparation d\'électroménagers.'
  },
  {
    year: '1980s',
    title: 'Expansion',
    description: 'Ajout de la vente d\'appareils remis à neuf aux services offerts.'
  },
  {
    year: '1999',
    title: 'Nouvelle génération',
    description: 'Eric Lebleu, fils des fondateurs, reprend les rênes de l\'entreprise après y avoir travaillé depuis l\'âge de 16 ans.'
  },
  {
    year: '2015',
    title: 'Déménagement',
    description: 'L\'entreprise quitte le Rang Double pour s\'installer dans des locaux plus spacieux sur la route 112.'
  },
  {
    year: 'Aujourd\'hui',
    title: 'Croissance continue',
    description: '8 employés dévoués et 5 camions de service pour répondre à tous vos besoins en CVC et électroménager.'
  }
];

const values = [
  {
    icon: Award,
    title: 'Qualité',
    description: 'Un travail bien fait, des équipements fiables et une satisfaction garantie.'
  },
  {
    icon: Users,
    title: 'Service',
    description: 'Une équipe dédiée à votre confort, disponible et à l\'écoute.'
  },
  {
    icon: Truck,
    title: 'Réactivité',
    description: 'Une intervention rapide avec nos 5 camions de service.'
  },
  {
    icon: Calendar,
    title: 'Engagement',
    description: 'Plus de 47 ans d\'expérience au service de notre communauté.'
  }
];

export const AboutPage = () => {
  return (
    <div>
      {/* Hero */}
      <section className="hero-gradient py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-20 w-72 h-72 bg-[#00b4d8] rounded-full blur-3xl"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Notre Histoire
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            Une entreprise familiale au service de votre confort depuis 1977
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#00b4d8] font-medium">Depuis 1977</span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a3a5c] mt-2 mb-6">
                Une passion transmise de génération en génération
              </h2>
              <p className="text-slate-600 mb-6">
                C'est en 1977 que Jean Lebleu, animé par une fibre entrepreneuriale et un 
                savoir-faire autodidacte, fonde Le Bleu des Champs à Saint-Césaire, avec sa 
                femme Huguette à ses côtés.
              </p>
              <p className="text-slate-600 mb-6">
                Pendant que Jean s'occupe de la réparation, Huguette assure le bon fonctionnement 
                du comptoir, du service à la clientèle, de la comptabilité… et s'occupe aussi 
                des enfants! C'est dans ce contexte de travail acharné qu'Eric, leur fils, 
                commence à travailler dans l'entreprise à l'âge de 16 ans.
              </p>
              <p className="text-slate-600">
                Aujourd'hui, Eric Lebleu veille sur l'entreprise familiale avec la même passion 
                qui animait ses parents. Son objectif est clair: offrir un service exceptionnel 
                qui fait une réelle différence dans le confort quotidien des gens.
              </p>
              
              <blockquote className="mt-8 p-6 bg-slate-50 rounded-2xl border-l-4 border-[#00b4d8]">
                <p className="text-lg italic text-[#1a3a5c]">
                  « J'aime aller à la rencontre des gens et savoir que notre entreprise fait 
                  une différence dans leur confort au quotidien! »
                </p>
                <cite className="text-slate-500 mt-4 block">— Eric Lebleu, Propriétaire</cite>
              </blockquote>
            </div>

            <div className="relative">
              <img 
                src="https://images.pexels.com/photos/20046691/pexels-photo-20046691.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Équipement HVAC"
                className="rounded-2xl shadow-xl"
              />
              <div className="absolute -bottom-8 -left-8 bg-[#1a3a5c] text-white rounded-2xl p-6 shadow-lg">
                <p className="text-4xl font-bold text-[#00d4ff]">47+</p>
                <p className="text-slate-300">Années d'expérience</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a3a5c]">
              Notre parcours
            </h2>
          </div>

          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-[#00b4d8]/30 hidden md:block"></div>

            <div className="space-y-12">
              {timeline.map((item, index) => (
                <div key={index} className="relative flex gap-8">
                  <div className="hidden md:flex flex-col items-center">
                    <div className="w-16 h-16 rounded-full bg-[#00b4d8] text-white flex items-center justify-center font-bold text-sm z-10">
                      {item.year}
                    </div>
                  </div>
                  <div className="flex-1 bg-white rounded-2xl p-6 shadow-sm border border-slate-100 card-hover">
                    <span className="md:hidden inline-block px-3 py-1 rounded-full bg-[#00b4d8] text-white text-sm font-medium mb-3">
                      {item.year}
                    </span>
                    <h3 className="text-xl font-semibold text-[#1a3a5c] mb-2">
                      {item.title}
                    </h3>
                    <p className="text-slate-600">
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#00b4d8] font-medium">Ce qui nous distingue</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a3a5c] mt-2">
              Nos valeurs
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div 
                  key={index}
                  className="text-center p-8 rounded-2xl bg-slate-50 card-hover"
                >
                  <div className="w-16 h-16 rounded-full bg-[#00b4d8]/10 flex items-center justify-center mx-auto mb-6">
                    <Icon className="w-8 h-8 text-[#00b4d8]" />
                  </div>
                  <h3 className="text-xl font-semibold text-[#1a3a5c] mb-3">
                    {value.title}
                  </h3>
                  <p className="text-slate-500">
                    {value.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-[#1a3a5c] to-[#0f172a]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Faites confiance à notre expertise
          </h2>
          <p className="text-slate-300 text-lg mb-8">
            Contactez-nous dès aujourd'hui pour discuter de vos besoins en climatisation, 
            chauffage, ventilation ou électroménager.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/soumission">
              <Button 
                size="lg" 
                className="rounded-full bg-[#00b4d8] hover:bg-[#009bb8] text-white px-8"
              >
                Demander une soumission
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <a href="tel:+14504693483">
              <Button 
                size="lg" 
                variant="outline" 
                className="rounded-full border-2 border-white/30 text-white hover:bg-white/10 px-8"
              >
                Appelez-nous
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
